import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material';
import { Button, Grid } from '@mui/material';
import { BrandKey } from '@prismamedia/one-brandkey';
import { DragDropContext, useOnMount } from '@prismamedia/one-components';
import { useEditContext } from '@prismamedia/one-crud-react';
import { Campaign } from '__generated__/queries-newsletter';
import { ArticleFormat, ArticleStatus } from '__generated__/queries-web';
import clsx from 'clsx';
import { FC, useEffect, useMemo, useReducer, useState } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from 'utils/useUriHelpers';
import { useStyles as useCommonStyle } from '../../../common/EditPage/styles';
import { ResourceList } from './ResourceList';
import { Sections } from './Sections';
import { actions, reducer } from './reducer';
import { useStyles } from './styles';
import { buildReducerState } from './utils/buildReducerState';
import { useDisplayArticles } from './utils/useDisplayArticles';
import {
  ResourceType,
  SearchParams,
  resources,
  useGetBlockItems,
} from './utils/useGetBlockItems';

export const ContentPage: FC = () => {
  const location = useLocation();
  const context = useEditContext();
  const classes = useStyles();
  const commonClasses = useCommonStyle();
  const campaign: Campaign = context.item as Campaign;
  const [resourceType, setResourceType] = useState<ResourceType>(
    resources[0].type,
  );
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [sections, dispatch] = useReducer(
    reducer,
    buildReducerState(campaign, location.pathname),
  );
  // eslint-disable-next-line immutable/no-mutation
  context.item = {
    ...context.item,
    sections,
  };

  const {
    showArticles,
    showSections,
    setShowArticles,
    isMobile,
  } = useDisplayArticles();

  const { queryParams, setQueryParam } = useQueryParams({
    brand: '',
    status: ArticleStatus.Published,
  });

  const brandKey =
    campaign.newsletter &&
    ((campaign.newsletter.brandKey
      ? campaign.newsletter.brandKey
      : queryParams.brand) as BrandKey);

  const articleFormat = useMemo(() => {
    if (resourceType === ResourceType.ARTICLE) {
      return ArticleFormat.Rich;
    } else if (resourceType === ResourceType.DIAPO) {
      return ArticleFormat.Slideshow;
    }

    return undefined;
  }, [resourceType]);

  const { blockItems, loading, error, fetchMore } = useGetBlockItems(
    articleFormat,
    resourceType,
    brandKey,
    searchParams,
    campaign.availableAt,
  );

  const sectionsBlocksArticleUrls = sections
    .flatMap(({ blocks }) => blocks)
    .map(({ url }) => url);

  const filteredBlockItems = useMemo(() => {
    const scheduledStatuses =
      queryParams.status === ArticleStatus.Scheduled
        ? [ArticleStatus.Scheduled, ArticleStatus.ScheduledForRepublication]
        : [queryParams.status];

    const newFilteredBlockItems =
      blockItems?.filter(
        ({ status, url, id }, index, source) =>
          // Remove article with wrong status
          scheduledStatuses.includes(status as ArticleStatus) &&
          // Remove dupplicate articles
          index === source.findIndex(({ id: sourceId }) => sourceId === id) &&
          // Remove articles without url or with an url already used in sections blocks
          (!url || !sectionsBlocksArticleUrls.includes(url)),
      ) || [];

    return queryParams.status === ArticleStatus.Scheduled
      ? // eslint-disable-next-line fp/no-mutating-methods
        [...newFilteredBlockItems].reverse()
      : newFilteredBlockItems;
  }, [blockItems, queryParams, sectionsBlocksArticleUrls]);

  const onDragEnd = (result: DropResult) => {
    if (filteredBlockItems) {
      dispatch(actions.dragEnd({ result, articles: filteredBlockItems }));
    }
  };

  const totalBlocksCount = sections.reduce(
    (acc, section) => section.blocks.length + acc,
    0,
  );

  const handleTabSelect = (tabId: string) => {
    setQueryParam('status', tabId, 'replace');
  };

  useOnMount(() => {
    if (!queryParams.status) {
      setQueryParam('status', ArticleStatus.Published);
    }
  });

  useEffect(() => {
    const defaultSectionCount =
      campaign.newsletter &&
      campaign.newsletter.isFromNlf &&
      campaign.newsletter?.sections
        ? JSON.parse(campaign.newsletter.sections).length
        : campaign.newsletter?.defaultCampaignSectionCount;

    if (!campaign.id && defaultSectionCount) {
      // Add sections
      if (sections.length < defaultSectionCount) {
        // eslint-disable-next-line fp/no-loops
        for (let i = 0; i < defaultSectionCount - sections.length; i++) {
          dispatch(actions.addSection());
        }
      }

      // Remove sections
      else if (sections.length > defaultSectionCount) {
        // eslint-disable-next-line fp/no-mutating-methods
        const sortedSections = sections.sort(
          (a, b) => b.blocks.length - a.blocks.length,
        );

        // eslint-disable-next-line fp/no-loops
        for (let i = 0; i < sections.length; i++) {
          const currentSection = sortedSections[i];

          if (i + 1 > defaultSectionCount && !currentSection.blocks.length) {
            dispatch(actions.deleteSection({ sectionId: currentSection.id }));
          }
        }
      }
    }

    const newsletterSectionConfig = context.item.newsletter.sections;

    dispatch(actions.regulateSectionsFromTemplate({ newsletterSectionConfig }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaign.id,
    campaign.newsletter,
    context.item.newsletter.sections,
    totalBlocksCount,
  ]);

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {showArticles && (
        <Grid item xs={12} md={6.5} className={classes.grid}>
          {isMobile && (
            <Button
              onClick={() => setShowArticles(false)}
              size="small"
              variant="outlined"
              color="primary"
              style={{ marginBottom: 10 }}
            >
              Voir les sections
              <ArrowForwardIos fontSize="small" />
            </Button>
          )}
          <ResourceList
            articleFormat={articleFormat}
            blockItems={filteredBlockItems}
            brandKey={brandKey}
            dispatch={dispatch}
            error={error}
            fetchMore={fetchMore}
            loading={loading}
            onTabSelect={handleTabSelect}
            resourceType={resourceType}
            searchParams={searchParams}
            sectionsIds={sections.map((section) => section.id)}
            setResourceType={setResourceType}
            setSearchParams={setSearchParams}
          />
        </Grid>
      )}
      {showSections && (
        <Grid
          item
          xs={12}
          md={5.5}
          className={clsx(classes.sections, commonClasses.grid)}
        >
          {isMobile && (
            <Button
              onClick={() => setShowArticles(true)}
              size="small"
              variant="outlined"
              color="primary"
            >
              <ArrowBackIos fontSize="small" />
              Retour à la recherche
            </Button>
          )}
          <Sections
            sections={sections}
            newsletterSectionConfig={context.item.newsletter.sections}
            dispatch={dispatch}
          />
        </Grid>
      )}
    </DragDropContext>
  );
};
